import React, { useContext, useState } from "react";
import styled, { css } from "styled-components";
import { RingSpinner } from "Components/RingSpinner";
import { EventContext, AnalyticsContext } from "context";
import { PrimaryTemplate as Layout } from "Layout/Templates";
import { MaxWidthContainer } from "Layout/MaxWidthContainer";
import {
  Modal,
  Media16By9,
  MediaVideo,
  ButtonPrimary,
  ButtonSecondary,
} from "Components";
import { RegistrationForm } from "Components/Form";
import { useMediaQuery } from "hooks";
import bannerCalloutBold from "assets/BannerCalloutBold.png";

export const RegistrationLayout = React.memo(
  ({
    sweeps,
    postSweeps,
    ineligible,
    formStep = 0,
    registered,
    postCampaign,
    setIneligible,
  }) => {
    const { event } = useContext(EventContext);
    const { logVideoAction, logClick } = useContext(AnalyticsContext);

    const [open, setOpen] = useState(false);
    const [video, setVideo] = useState(null);
    const [focus, setFocus] = useState(false);
    const [ref, setRef] = useState(false);
    const [loading, setLoading] = useState(false);

    const api = process.env.REACT_APP_FIRE_BASE_STORAGE_API;
    const storageDirectory = event.meta.storageDirectory;
    const isMobile = useMediaQuery();
    const getAssetUrl = (asset) => `${api}/${storageDirectory}/${asset}`;

    // logo
    const { brand: brandLogo } = event.logos;
    let brandLogoImg = `${api}/${storageDirectory}/${brandLogo?.image}`;
    let brandLogoWhite = `${api}/${storageDirectory}/${brandLogo?.logoWhite}`;
    let logoFest = `${api}/${storageDirectory}/${brandLogo?.logoFest}`;

    const {
      heroBgPre,
      heroBgPreMob,
      heroBgPostReg,
      heroBgPostRegMob,
      heroBgPostCamp,
      heroBgPostCampMob,
      calloutImage,
      calloutQuote,
      switchImage,
      partPattern,
      partPatternMob,
      songPattern,
      playButton,
      songImage,
      contentBg1: img1,
      contentBg2: img2,
    } = event.images;
    let heroImgPre = `${api}/${storageDirectory}/${heroBgPre}`;
    let heroImgPreMob = `${api}/${storageDirectory}/${heroBgPreMob}`;
    let heroImgPostReg = `${api}/${storageDirectory}/${heroBgPostReg}`;
    let heroImgPostRegMob = `${api}/${storageDirectory}/${heroBgPostRegMob}`;
    let heroImgPostCamp = `${api}/${storageDirectory}/${heroBgPostCamp}`;
    let heroImgPostCampMob = `${api}/${storageDirectory}/${heroBgPostCampMob}`;
    let calloutImg = `${api}/${storageDirectory}/${calloutImage}`;
    let quoteCalloutImg = `${api}/${storageDirectory}/${calloutQuote}`;
    let switchImg = `${api}/${storageDirectory}/${switchImage}`;
    let playButtonIcon = `${api}/${storageDirectory}/${playButton}`;
    let songImg = `${api}/${storageDirectory}/${songImage}`;
    let partialPattern = `${api}/${storageDirectory}/${partPattern}`;
    let songPatternImg = `${api}/${storageDirectory}/${songPattern}`;
    let partialPatternMobile = `${api}/${storageDirectory}/${partPatternMob}`;
    let contentBg1 = `${api}/${storageDirectory}/${img1}`;
    let contentBg2 = `${api}/${storageDirectory}/${img2}`;

    if (registered) formStep = event.register.sweeps.length - 1;
    // registration content
    const {
      heading,
      subHeading,
      body,
      formHeading,
      formLegal,
      submit,
    } = event.register.sweeps[formStep];

    // music video background
    const {
      background: musicVideoBg,
      label: musicVideoLabel,
      video: musicVideoLink,
    } = event.callToActions.musicVideo;
    let videoBgDesktop = `${api}/${storageDirectory}/${musicVideoBg.desktop}?alt=media`;
    let videoBgMobile = `${api}/${storageDirectory}/${musicVideoBg.mobile}?alt=media`;

    // artist cta
    const { heroVideoLink } = event.callToActions.artists;

    // brand cta background
    const {
      desktop: brandBgD,
      mobile: brandBgM,
    } = event.callToActions.brand.background;
    let brandBgDesktop = `${api}/${storageDirectory}/${brandBgD}?alt=media`;
    let brandBgMobile = `${api}/${storageDirectory}/${brandBgM}?alt=media`;

    // brand product background
    const {
      desktop: brandProductDesktop,
      mobile: brandProductMobile,
    } = event.callToActions.brand.product;
    let productDesktop = `${api}/${storageDirectory}/${brandProductDesktop}?alt=media`;
    let productMobile = `${api}/${storageDirectory}/${brandProductMobile}?alt=media`;

    // post-campaign content
    const {
      heading: postCampaignHeading,
      body: postCampaignBody,
    } = event.postCampaign;

    // brand cta (post-campaign)
    const {
      btnLink: brandSecondaryCTALink,
      btnLinkPostCampaign: PostCampaignCTALink,
      btnText: brandSecondaryCTAText,
      heading: brandSecondaryCTAHeading,
    } = event.callToActions.brandSecondary;

    const linkClickHandler = (url, label, category) => {
      logClick({ label, url }, category);
    };

    const setModalVisible = (bool, link) => {
      setVideo(link);
      setOpen(bool);
    };
    const scrollToSection = () => {
      if (ref) {
        window.scrollTo({ top: ref?.current?.offsetTop });
        ref.focus();
        setFocus(true);
      }
    };

    if (loading) {
      window.scrollTo(0, 0);
      return <RingSpinner />;
    }

    return (
      <Layout
        registration={postCampaign ? false : true}
        flexGrow
        showHeader
        postCampaign={postCampaign}
      >
        <Container postCampaign={postCampaign}>
          <SkipLink
            href="#footer"
            aria-label="link to skip to footer from main"
            style={{ color: "#fff !important" }}
          >
            Skip to footer content
          </SkipLink>
          <HeroContainer>
            {postCampaign ? (
              <>
                <HeroBackground
                  src={isMobile ? heroImgPostCampMob : heroImgPostCamp}
                  alt="Artist HARDY in front of a grey wall"
                />
                <HeroRegContent>
                  <Heading tabIndex={0}>
                    SWEEPSTAKES REGISTRATION IS CLOSED
                  </Heading>
                  <Body>
                    The sweepstakes may be over, but the conversation continues.
                    Learn about our mission to foster genuine connections over
                    digital distractions below.
                  </Body>
                </HeroRegContent>
              </>
            ) : registered ? (
              <>
                <HeroBackground
                  src={isMobile ? heroImgPostRegMob : heroImgPostReg}
                  alt="Artist HARDY with a notepad and a pen"
                />
                <HeroRegContent>
                  <Heading>REGISTRATION CONFIRMED</Heading>
                  <Body>
                    You've officially entered. Stay tuned-you might be packing
                    your bags for a VIP experience.
                  </Body>
                </HeroRegContent>
              </>
            ) : (
              <>
                <HeroBackground
                  src={isMobile ? heroImgPreMob : heroImgPre}
                  alt="Artist HARDY with a guitar"
                />
                <HeroRegContent preReg>
                  <MainHeading tabIndex={0}>
                    Hardy <br />
                    VIP Fan <br />
                    Experience
                  </MainHeading>
                  <Body>
                    Scroll down for details.
                    <br />
                    For Official Rules:{" "}
                    <a
                      href="rules"
                      aria-label="button to open link in a new window to read sweepstakes rules"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      lifeinusmode.uscellular.com/rules{" "}
                    </a>
                  </Body>
                  <ButtonPrimary
                    tabIndex={0}
                    style={{ margin: "30px auto 10px 0" }}
                    onClick={() => {
                      scrollToSection();
                      linkClickHandler(
                        "Anchor to form",
                        "Button Anchor to form",
                        "internal"
                      );
                    }}
                    aria-label="Enter here Button to automatically scroll to fill up the form to win tickets at Summerfest"
                    role="content main"
                  >
                    Enter Here
                  </ButtonPrimary>
                </HeroRegContent>
              </>
            )}{" "}
          </HeroContainer>

          <BannerTopContainer>
            <BrandLogoWhite src={brandLogoWhite} alt="" />
            <InfoContainer>
              <CalloutImage src={calloutImg} alt="" />
              <FlexRow>
                {registered || postCampaign ? (
                  <Body>
                    Join UScellular<sup>&reg;</sup> and HARDY in a movement
                    towards more authentic connections. Discover how HARDY
                    balances life on and off the screen, living Life in US Mode
                    by nurturing meaningful relationships over constant
                    connection.
                  </Body>
                ) : (
                  <Body>
                    UScellular<sup>&reg;</sup> and HARDY have partnered to
                    inspire genuine connections in a digital world. Discover how
                    HARDY balances life on and off the screen, living Life in US
                    Mode to nurture meaningful relationships over constant
                    connection.
                  </Body>
                )}
                <ButtonSecondary
                  tabIndex={0}
                  as="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  href="https://www.youtube.com/watch?v=YT7ua8_dO10"
                  role="link"
                  aria-label="Watch artist Hardy story on youtube in a new window"
                  onClick={() =>
                    linkClickHandler("", "Artist Story", "external")
                  }
                >
                  WATCH HARDY’S STORY
                </ButtonSecondary>
              </FlexRow>
              <SwitchContainer>
                <img src={switchImg} alt="" />
              </SwitchContainer>
            </InfoContainer>
          </BannerTopContainer>

          {!registered && !postCampaign && (
            <FormContainer>
              {isMobile ? (
                <div>
                  <PartPatternImgMob src={partialPatternMobile} alt="" rotate />
                </div>
              ) : (
                <FormPattern src={songPatternImg} alt="" />
              )}
              <FormWrapper>
                <Heading tabIndex={0}>Hardy VIP FAN Experience</Heading>
                <Body tabIndex={0}>
                  Enter for your chance to win concert tickets to see HARDY, a
                  $1,500 prepaid gift card, signed HARDY merch, transportation
                  and hotel accommodations.
                </Body>
                <Body tabIndex={0}>
                  The Sweepstakes is sponsored solely by Music Audience
                  Exchange, Inc., UScellular is not a sponsor.
                </Body>
                <RegistrationForm
                  formPart={formStep}
                  formHeading={formHeading}
                  formLegal={formLegal}
                  submitButton={submit}
                  setIneligible={setIneligible}
                  focus={focus}
                  setLoading={setLoading}
                  setRef={setRef}
                />
              </FormWrapper>
              {!isMobile && <PartPatternImg src={partialPattern} alt="" />}
              {isMobile && (
                <div>
                  <PartPatternImgMob src={partialPatternMobile} alt="" />
                </div>
              )}
            </FormContainer>
          )}

          <ContentUSC>
            <ContainerUsMode
              bg={contentBg1}
              alt="Artist HARDY with a guitar sitting on a chair"
            >
              <Content id="CTA">
                <Logo src={brandLogoImg} alt="" />
                <Heading tabIndex={0}>What is US Mode?</Heading>
                <Body>
                  Ever feel like endless alerts are keeping you from being in
                  the moment? Enter US Mode by UScellular – a setting that helps
                  you regain control of your notifications to limit
                  distractions. Personalize your notification settings to reduce
                  interruptions so you can concentrate on connecting to what
                  matters most.
                </Body>
                <ButtonPrimary
                  tabIndex={0}
                  as="a"
                  target="_blank"
                  rel="noopener noreferrer"
                  role="link"
                  aria-label="UScellular US Mode Link opens in a new window"
                  href="https://www.uscellular.com/USMode"
                  onClick={() =>
                    linkClickHandler(
                      "https://www.uscellular.com/USMode",
                      "UScellular US Mode",
                      "external"
                    )
                  }
                >
                  Learn more
                </ButtonPrimary>
              </Content>
              {isMobile && (
                <img
                  id="mobileBgImg"
                  src={contentBg1}
                  alt="Artist HARDY with a guitar sitting on a chair"
                />
              )}
            </ContainerUsMode>
          </ContentUSC>

          <ContentQuote>
            {isMobile && (
              <FlexColumnDark>
                <CalloutImage src={bannerCalloutBold} alt="" />
                <QuoteCalloutImage
                  src={getAssetUrl("quote-mobile.png")}
                  alt=""
                />
              </FlexColumnDark>
            )}
            <ImageContainer>
              <img src={contentBg2} alt="" />
            </ImageContainer>
            <QuoteWrapper>
              <FlexColumn>
                <BrandLogoWhite src={brandLogoWhite} alt="" />
                <Heading>CONNECT WITH WHAT MATTERS MOST</Heading>
                <Body>
                  Dedicate time for real-life relationships and interactions,
                  enjoy the people and world around you.
                </Body>
              </FlexColumn>
              {!isMobile && (
                <FlexColumnDark>
                  <CalloutImage src={bannerCalloutBold} alt="" />
                  <QuoteCalloutImage
                    src={getAssetUrl("quote-desktop.png")}
                    alt=""
                  />
                </FlexColumnDark>
              )}
            </QuoteWrapper>
          </ContentQuote>

          <ArtistSongContainer>
            <FlexColumnLight>
              <Float>
                <Heading>MAKE EVERY MOMENT COUNT</Heading>
                <Body>
                  Unplug and break free from the digital noise. Be present to
                  fully experience life and the memories we create.
                </Body>
              </Float>
              <img src={songPatternImg} alt="" />
            </FlexColumnLight>
            <SongImage
              as="a"
              role="button"
              aria-label="Hardy song on Youtube opens in a new window"
              background={songImg}
              href="https://www.youtube.com/watch?v=rk3nqoZZHhU"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() =>
                linkClickHandler(
                  "https://www.youtube.com/watch?v=rk3nqoZZHhU",
                  "Artist song",
                  "external"
                )
              }
            >
              <img src={playButtonIcon} alt="" />
              <Heading>Listen to "Screen"</Heading>
            </SongImage>
          </ArtistSongContainer>

          <ContainerPadding>
            <BannerContainer>
              <FlexColumn style={{ padding: 0 }}>
                <BrandLogoWhite
                  src={brandLogoWhite}
                  alt=""
                  style={{ marginTop: "-30px" }}
                />
                <InfoWrapper>
                  <Heading>
                    Built for{" "}
                    <span>
                      US<sup>TM</sup>
                    </span>
                  </Heading>
                  <FloatButton>
                    <ButtonSecondary
                      tabIndex={0}
                      as="a"
                      target="_blank"
                      role="link"
                      rel="noopener noreferrer"
                      href="https://www.uscellular.com/BuiltforUS"
                      aria-label="UScellular Built for Us Link opens in a new window"
                      onClick={() =>
                        linkClickHandler(
                          "https://www.uscellular.com/BuiltForUS",
                          "UScellular Built for Us",
                          "external"
                        )
                      }
                    >
                      Learn more
                    </ButtonSecondary>
                  </FloatButton>
                </InfoWrapper>
              </FlexColumn>
              <Body>
                UScellular is built for genuine connections. By emphasizing
                meaningful interactions over digital distractions, we’re
                bridging the gap between staying connected and nurturing
                real-world relationships. In other words, we’re built to make us
                feel a little more like US again.
              </Body>
            </BannerContainer>
          </ContainerPadding>
        </Container>

        <Modal open={open} setOpen={setModalVisible}>
          <Media16By9>
            <MediaVideo
              src={video}
              frameborder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowfullscreen
            />
          </Media16By9>
        </Modal>
      </Layout>
    );
  }
);

const Container = styled(MaxWidthContainer)`
  display: flex;
  flex: 1;
  flex-direction: column;
  background-color: #fff;
  position: relative;
  @media ${(props) => props.theme.maxMedia.medium} {
    padding: 0;
  }
`;
const SkipLink = styled.a`
  color: #ffffff00 !important;
  position: absolute;
  display: block;
  height: 20px;
  top: 0;
  bottom: 0;
  z-index: 0;
  &:visited,
  &:active {
    color: #ffffff00 !important;
  }
`;
const BannerTopContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #0731a1;
  padding: 20px 0 40px;
`;
const BrandLogoWhite = styled.img`
  width: clamp(250px, 20%, 300px);
  object-fit: cover;
  padding: 20px 0;
`;
const FloatButton = styled.div`
  position: absolute;
  bottom: -40px;
  margin: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50%;
  padding: 20px;
  min-width: 200px;
  background-color: #0731a1;
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 70%;
    max-width: 240px;
  }
`;
const InfoContainer = styled.div`
  width: 80%;
  margin: 20px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 4px solid #82dcff;
  position: relative;
  padding: 25px 25px 55px;
`;
const InfoWrapper = styled.div`
  width: 80%;
  margin: 20px auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border: 4px solid #82dcff;
  position: relative;
  padding: 20px;
  @media ${(props) => props.theme.maxMedia.medium} {
    max-width: 600px;
    padding: 40px 20px 60px;
    width: 100%;
  }
`;
const BannerContainer = styled.div`
  width: clamp(750px, 90%, 1000px);
  margin: auto;
  display: flex;
  gap: 30px;
  align-items: center;
  margin: 0px auto;
  h2,
  p {
    color: #fff;
  }
  p {
    margin-top: 80px;
    width: 60%;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    flex-direction: column;
    width: 100%;
    p {
      margin-top: 0px;
      width: 80%;
      text-align: center;
    }
  }
`;
const CalloutImage = styled.img`
  width: 30%;
  object-fit: cover;
  padding: 20px 0;
  min-width: 200px;
`;
const QuoteCalloutImage = styled.img`
  width: 60%;
  object-fit: cover;
  min-width: 300px;
`;
const FlexColumn = styled.div`
  width: 80%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  background-color: #0731a1;
  margin: auto;
  padding: 20px;
  position: relative;
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
  }
`;
const FlexColumnLight = styled.div`
  width: 45%;
  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  margin: auto;
  padding-top: 60px;
  position: relative;
  img {
    width: 100%;
    margin-top: 120px;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
  }
`;
const QuoteWrapper = styled.div`
  width: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #0731a1;
  h2 {
    color: #82dcff;
    margin: 0;
    font-size: 37px;
  }
  p {
    color: #ffffff;
  }
  & > div {
    background-color: #0731a1;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
  }
`;
const FlexColumnDark = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: #1b2a55 !important;
  margin-top: auto;
  padding: 20px 0 40px;
`;
const ContentQuote = styled.div`
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  @media ${(props) => props.theme.maxMedia.medium} {
    flex-direction: column-reverse;
  }
`;
const ArtistSongContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: center;
  @media ${(props) => props.theme.maxMedia.medium} {
    flex-direction: column-reverse;
  }
`;
const Float = styled.div`
  position: absolute;
  width: 80%;
  top: 20%;
  margin: auto;
  z-index: 10;
`;
const ImageContainer = styled.div`
  width: 50%;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
    margin-bottom: -10px;
  }
`;
const FlexRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 70px;
  padding: 0 20px;
  p {
    color: #fff;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    flex-direction: column;
    gap: 10px;
    p {
      text-align: center;
    }
  }
`;
const SongImage = styled.div`
  width: 55%;
  background: ${(props) => `url(${props.background})`};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  cursor: pointer;
  background-size: cover;
  background-position: top center;
  img {
    width: 120px;
    margin: 60px auto 20px;
  }
  h2 {
    color: #ffffff;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
    text-align: center;
    min-height: 600px;
  }
`;
const SwitchContainer = styled.div`
  position: absolute;
  margin-left: auto;
  margin-right: auto;
  bottom: -50px;
  padding: 20px;
  background-color: #0731a1;
  width: 40%;
  max-width: 400px;
  img {
    width: 100%;
    object-fit: cover;
    margin: auto;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 80%;
  }
`;

const Content = styled.div`
  width: 50%;
  max-width: 550px;
  display: flex;
  flex-direction: column;
  border-radius: 20px;
  background-color: #fff;
  align-items: center;
  &#CTA {
    align-items: flex-start;
  }
  padding: 45px 45px 65px;
  img {
    max-width: 180px;
  }
  p {
    max-width: 500px;
    margin-top: -5px;
    margin-bottom: 35px;
  }
  h2 {
    width: 100%;
    max-width: 500px;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
    padding: 20px;
    background-color: #fff;
    margin: auto;
    text-align: center;
    align-items: center !important;
    h2 {
      margin: 30px auto 30px 0;
    }
    img {
      margin: 10px auto;
    }
    p {
      margin-left: auto !important;
      margin-right: auto !important;
    }
  }
`;
const ContentUSC = styled.div`
  width: 100%;
  position: relative;
`;

const HeroContainer = styled.div`
  width: 100%;
  position: relative;
  display: flex;
  @media ${(props) => props.theme.maxMedia.medium} {
    flex-direction: column-reverse;
  }
`;

const HeroRegContent = styled.div`
  width: 40%;
  max-width: 550px;
  position: absolute;
  top: 30%;
  left: 5vw;
  z-index: 10;
  padding: 35px;
  border-radius: 6px;
  max-width: 450px;
  h1 {
    color: #0731a1 !important;
    margin: 0;
  }
  p,
  a {
    color: #0731a1;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    top: unset;
    bottom: 6%;
    width: 80%;
    left: 3%;
    padding: 15px;
  }
  ${({ preReg }) =>
    preReg &&
    css`
      top: 18%;
    `}
`;
const HeroBackground = styled.img`
  min-height: 500px;
  max-height: 1070px;
  width: 100%;
  object-fit: cover;
  object-position: top;
  z-index: 2;
`;
const PartPatternImg = styled.img`
  display: block;
  width: 65%;
  object-fit: cover;
  z-index: 2;
  margin-right: auto;
`;
const PartPatternImgMob = styled.img`
  display: block;
  width: 100%;
  object-fit: cover;
  z-index: 2;
  ${({ rotate }) =>
    rotate &&
    css`
      transform: rotate(180deg);
    `}
`;

const FormPattern = styled.img`
  position: absolute;
  right: 0;
  top: 0;
  z-index: 1;
  width: 30%;
  transform: rotate(180deg);
`;
const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  position: relative;
  @media ${(props) => props.theme.maxMedia.medium} {
  }
`;
const FormWrapper = styled.div`
  width: 100%;
  display: flex;
  max-width: 1000px;
  margin: auto;
  padding: 50px 20px 20px;
  z-index: 10;
  position: relative;
  flex-direction: column;
  text-align: center;
  & > p {
    margin: -20px 20px 30px;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    width: 100%;
    & > p {
      margin: 20px 0px 0;
    }
  }
`;

const ContainerPadding = styled.div`
  width: 100%;
  background-color: #0731a1;
  padding: 50px 30px;
  @media ${(props) => props.theme.maxMedia.medium} {
  }
`;
const ContainerUsMode = styled.div`
  background-image: url(${(props) => props.bg});
  background-size: cover;
  background-repeat: no-repeat;
  background-position: top 0 right 40%;
  padding: 60px 70px 100px;
  position: relative;
  img#rotate {
    width: 45%;
    rotate: 180deg;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
  }

  @media ${(props) => props.theme.maxMedia.medium} {
    background: #fff;
    padding: 0;
    & > img {
      object-fit: cover;
      width: 100%;
      margin: 25px 0 -7px 0;
    }
    & > img#mobileBgImg {
      object-fit: cover;
      width: 100%;
      height: 400px;
      object-position: top right;
    }
    img#rotate {
      width: 95%;
      rotate: 180deg;
      position: relative;
      object-fit: cover;
      margin: 0;
      top: 0;
      left: 0;
    }
  }
`;
const Logo = styled.img`
  display: flex;

  @media (max-width: 950px) {
    margin: 0 auto;
  }
`;

const MainHeading = styled.h1`
  font-size: min(5.6vw, 64px);
  font-family: "BrandonBlk" !important;
  text-transform: uppercase;

  & > span {
    display: block;
    font-size: 20px;
    padding-top: 10px;
  }

  @media ${(props) => props.theme.maxMedia.medium} {
    font-size: 40px;

    & > span {
      font-size: 1.2rem;
    }
  }
`;

const Heading = styled.h2`
  font-size: min(3.8vw, 48px);
  text-transform: uppercase;
  font-family: "BrandonBlk";
  display: block;
  span {
    color: #82dcff;
  }
  sup {
    font-size: 12px;
    vertical-align: super;
    position: relative;
    top: -11px;
  }
  @media ${(props) => props.theme.maxMedia.medium} {
    font-size: 36px;
    margin: 0;
    sup {
      font-size: 12px;
      vertical-align: super;
      position: relative;
      top: -6px;
      margin-left: 2px;
    }
  }
`;

const Body = styled.p`
  font-size: 20px;
  font-family: "BrandonReg";

  sup {
    font-size: 12px;
  }

  span.headline {
    font-size: 30px;
    color: #0731a1;
    display: inline-block;
    margin: 25px 0 10px;
    font-family: "BrandonMed";
  }
  span.super {
    font-size: 12px;
    display: inline-block;
    margin: -10px 0 0 0;
    position: relative;
    top: -5px;
  }
  ${({ float }) =>
    float &&
    css`
      font-size: min(1.7vw, 20px);
    `}
    @media ${(props) => props.theme.maxMedia.medium}{
      font-size: 20px;
    }
`;
